import { useState } from 'react'
import {
  BookOpen,
  Zap,
  Database,
  Play,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Send,
  Loader2,
  Webhook,
  Key,
  AlertCircle,
  CheckCircle,
  Terminal,
} from 'lucide-react'
import toast from 'react-hot-toast'

// ─── Types ──────────────────────────────────────────────────────────

interface PlaygroundResponse {
  status: number
  body: any
  time: number
}

type ActiveSection = 'channels' | 'data'

// ─── Component ──────────────────────────────────────────────────────

export function ApiDocsPage() {
  const [activeSection, setActiveSection] = useState<ActiveSection>('channels')
  const [expandedEndpoints, setExpandedEndpoints] = useState<Set<string>>(new Set(['ch-flow']))
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Playground state
  const [pgEndpoint, setPgEndpoint] = useState('')
  const [pgMethod, setPgMethod] = useState<'GET' | 'POST' | 'DELETE'>('POST')
  const [pgHeaders, setPgHeaders] = useState('{}')
  const [pgBody, setPgBody] = useState('')
  const [pgResponse, setPgResponse] = useState<PlaygroundResponse | null>(null)
  const [pgLoading, setPgLoading] = useState(false)

  // Quick-test state for custom channel
  const [testToken, setTestToken] = useState('')
  const [testMessage, setTestMessage] = useState('')
  const [testCustomerId, setTestCustomerId] = useState('test-customer-1')
  const [testResponse, setTestResponse] = useState<PlaygroundResponse | null>(null)
  const [testLoading, setTestLoading] = useState(false)

  // Quick-test state for data push
  const [dataApiKey, setDataApiKey] = useState('')
  const [dataIntegrationId, setDataIntegrationId] = useState('')
  const [dataItems, setDataItems] = useState(JSON.stringify([
    { external_id: 'item-001', item_type: 'product', data: { name: 'Sample Product', price: 29.99, description: 'A sample item' } }
  ], null, 2))
  const [dataResponse, setDataResponse] = useState<PlaygroundResponse | null>(null)
  const [dataLoading, setDataLoading] = useState(false)

  // Data list test
  const [listApiKey, setListApiKey] = useState('')
  const [listIntegrationId, setListIntegrationId] = useState('')
  const [listResponse, setListResponse] = useState<PlaygroundResponse | null>(null)
  const [listLoading, setListLoading] = useState(false)

  const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api').replace(/\/api$/, '')

  const toggleEndpoint = (id: string) => {
    setExpandedEndpoints(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code)
    setCopiedId(id)
    toast.success('Copied!')
    setTimeout(() => setCopiedId(null), 2000)
  }

  // ─── Playground: Send raw request ─────────────────────────────────

  const sendPlaygroundRequest = async () => {
    if (!pgEndpoint) { toast.error('Enter an endpoint URL'); return }
    setPgLoading(true)
    setPgResponse(null)
    const start = Date.now()
    try {
      let headers: Record<string, string> = { 'Content-Type': 'application/json' }
      try { headers = { ...headers, ...JSON.parse(pgHeaders) } } catch {}

      const url = pgEndpoint.startsWith('http') ? pgEndpoint : `${API_BASE}${pgEndpoint}`
      const opts: RequestInit = { method: pgMethod, headers }
      if (pgMethod !== 'GET' && pgBody.trim()) opts.body = pgBody

      const res = await fetch(url, opts)
      const body = await res.json().catch(() => res.text())
      setPgResponse({ status: res.status, body, time: Date.now() - start })
    } catch (err: any) {
      setPgResponse({ status: 0, body: { error: err.message }, time: Date.now() - start })
    } finally {
      setPgLoading(false)
    }
  }

  // ─── Quick Test: Custom Channel Chat ──────────────────────────────

  const sendTestChat = async () => {
    if (!testToken.trim()) { toast.error('Enter a channel token'); return }
    if (!testMessage.trim()) { toast.error('Enter a message'); return }
    setTestLoading(true)
    setTestResponse(null)
    const start = Date.now()
    try {
      const res = await fetch(`${API_BASE}/api/chat/public/${testToken.trim()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: testCustomerId.trim() || 'test-customer',
          message: testMessage.trim(),
        }),
      })
      const body = await res.json().catch(() => ({}))
      setTestResponse({ status: res.status, body, time: Date.now() - start })
    } catch (err: any) {
      setTestResponse({ status: 0, body: { error: err.message }, time: Date.now() - start })
    } finally {
      setTestLoading(false)
    }
  }

  // ─── Quick Test: Data Push ────────────────────────────────────────

  const sendDataPush = async () => {
    if (!dataApiKey.trim()) { toast.error('Enter your API token'); return }
    if (!dataIntegrationId.trim()) { toast.error('Enter integration ID'); return }
    setDataLoading(true)
    setDataResponse(null)
    const start = Date.now()
    try {
      let items: any[]
      try { items = JSON.parse(dataItems) } catch { toast.error('Invalid JSON for items'); setDataLoading(false); return }

      const res = await fetch(`${API_BASE}/api/v1/data/push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${dataApiKey.trim()}`,
        },
        body: JSON.stringify({ integration_id: dataIntegrationId.trim(), items }),
      })
      const body = await res.json().catch(() => ({}))
      setDataResponse({ status: res.status, body, time: Date.now() - start })
    } catch (err: any) {
      setDataResponse({ status: 0, body: { error: err.message }, time: Date.now() - start })
    } finally {
      setDataLoading(false)
    }
  }

  // ─── Quick Test: Data List ────────────────────────────────────────

  const sendDataList = async () => {
    if (!listApiKey.trim()) { toast.error('Enter your API token'); return }
    if (!listIntegrationId.trim()) { toast.error('Enter integration ID'); return }
    setListLoading(true)
    setListResponse(null)
    const start = Date.now()
    try {
      const res = await fetch(`${API_BASE}/api/v1/data/items?integration_id=${listIntegrationId.trim()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${listApiKey.trim()}`,
        },
      })
      const body = await res.json().catch(() => ({}))
      setListResponse({ status: res.status, body, time: Date.now() - start })
    } catch (err: any) {
      setListResponse({ status: 0, body: { error: err.message }, time: Date.now() - start })
    } finally {
      setListLoading(false)
    }
  }

  // ─── Code blocks ─────────────────────────────────────────────────

  const CodeBlock = ({ code, id }: { code: string; lang?: string; id: string }) => (
    <div className="relative group">
      <button
        onClick={() => copyCode(code, id)}
        className="absolute top-2 right-2 p-1.5 bg-gray-700 hover:bg-gray-600 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copiedId === id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-gray-300" />}
      </button>
      <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 text-sm overflow-x-auto font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  )

  const ResponseViewer = ({ response }: { response: PlaygroundResponse | null }) => {
    if (!response) return null
    const isSuccess = response.status >= 200 && response.status < 300
    return (
      <div className={`mt-4 rounded-xl border-2 ${isSuccess ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#1a1a2e]">
          <div className="flex items-center gap-2">
            {isSuccess ? <CheckCircle className="w-4 h-4 text-green-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
            <span className={`text-sm font-semibold ${isSuccess ? 'text-green-700' : 'text-red-700'}`}>
              {response.status || 'Error'} {isSuccess ? 'OK' : 'Failed'}
            </span>
          </div>
          <span className="text-xs text-gray-500">{response.time}ms</span>
        </div>
        <pre className="p-4 text-sm font-mono overflow-x-auto text-gray-800 max-h-80 overflow-y-auto">
          {typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2)}
        </pre>
      </div>
    )
  }

  const MethodBadge = ({ method }: { method: string }) => {
    const colors: Record<string, string> = {
      GET: 'bg-green-100 text-green-700',
      POST: 'bg-blue-100 text-blue-700',
      PUT: 'bg-amber-100 text-amber-700',
      DELETE: 'bg-red-100 text-red-700',
    }
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-bold font-mono ${colors[method] || 'bg-gray-100 text-gray-700'}`}>
        {method}
      </span>
    )
  }

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-blue-600" />
          API Documentation
        </h1>
        <p className="text-gray-500 mt-1">Custom API Channels & Data Integration - endpoints, flows, and live playground</p>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-[#1a1a2e]">
        <button
          onClick={() => setActiveSection('channels')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
            activeSection === 'channels'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Zap className="w-4 h-4" />
          Custom API Channels
        </button>
        <button
          onClick={() => setActiveSection('data')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
            activeSection === 'data'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Database className="w-4 h-4" />
          Data Integration API
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          CUSTOM API CHANNELS SECTION
         ═══════════════════════════════════════════════════════════════ */}
      {activeSection === 'channels' && (
        <div className="space-y-6">
          {/* Overview */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-indigo-600" />
              Custom API Channels
            </h2>
            <p className="text-gray-600 mb-4">
              Custom API channels let you integrate your AI agents with any external system. Create a channel, get a unique token,
              and send messages via a simple REST API. You can create as many channels as you need - one per app, per client, per use-case.
            </p>

            {/* Flow Diagram */}
            <div className="bg-indigo-50 rounded-xl p-6 mb-4">
              <h3 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide">How It Works</h3>
              <div className="flex flex-col gap-4">
                {/* Step 1 */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                  <div>
                    <p className="font-semibold text-gray-900">Create a Custom Channel</p>
                    <p className="text-sm text-gray-600">Go to <strong>Channels</strong> page &rarr; Connect Channel &rarr; Custom API. Give it a name, assign agents, and optionally set a webhook URL for async responses.</p>
                    <p className="text-sm text-gray-500 mt-1">You'll receive a unique <code className="bg-white px-1.5 py-0.5 rounded text-indigo-700 text-xs font-mono">channel_token</code>.</p>
                  </div>
                </div>
                {/* Step 2 */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                  <div>
                    <p className="font-semibold text-gray-900">Send Messages via API</p>
                    <p className="text-sm text-gray-600">POST to <code className="bg-white px-1.5 py-0.5 rounded text-indigo-700 text-xs font-mono">/api/chat/public/&#123;channel_token&#125;</code> with a customer_id and message. No authentication needed - the token validates the channel.</p>
                  </div>
                </div>
                {/* Step 3 */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
                  <div>
                    <p className="font-semibold text-gray-900">Get AI Responses</p>
                    <p className="text-sm text-gray-600">
                      <strong>Sync mode:</strong> Full AI response returned directly in the HTTP response.<br />
                      <strong>Async mode:</strong> Returns immediately with <code className="bg-white px-1.5 py-0.5 rounded text-xs font-mono">conversation_id</code>, AI response delivered to your webhook URL via POST with HMAC signature.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sync vs Async */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border-2 border-[#1a1a2e] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-blue-600" />
                  <h4 className="font-semibold text-blue-800">Sync Mode</h4>
                </div>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>- No webhook URL configured</li>
                  <li>- AI response returned in same HTTP call</li>
                  <li>- Response may take 2-10 seconds</li>
                  <li>- Best for: chatbots, internal tools</li>
                </ul>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Webhook className="w-4 h-4 text-purple-600" />
                  <h4 className="font-semibold text-purple-800">Async Mode</h4>
                </div>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>- Webhook URL configured on channel</li>
                  <li>- Returns immediately with conversation_id</li>
                  <li>- Full response POSTed to your webhook</li>
                  <li>- Best for: mobile apps, queues, high volume</li>
                </ul>
              </div>
            </div>
          </div>

          {/* ─── Endpoint: POST /api/chat/public/:token ─────────────── */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
            <button
              onClick={() => toggleEndpoint('ch-send')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                {expandedEndpoints.has('ch-send') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <MethodBadge method="POST" />
                <code className="text-sm font-mono text-gray-800">/api/chat/public/&#123;channel_token&#125;</code>
              </div>
              <span className="text-xs text-gray-500">Send a message to your custom channel</span>
            </button>

            {expandedEndpoints.has('ch-send') && (
              <div className="border-t border-[#1a1a2e] p-5 space-y-5">
                {/* Auth info */}
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">No authentication required - channel token in URL validates access</span>
                </div>

                {/* Request */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Request Body</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#1a1a2e]">
                          <th className="text-left py-2 px-3 text-gray-500 font-medium">Field</th>
                          <th className="text-left py-2 px-3 text-gray-500 font-medium">Type</th>
                          <th className="text-left py-2 px-3 text-gray-500 font-medium">Required</th>
                          <th className="text-left py-2 px-3 text-gray-500 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono text-xs">
                        <tr className="border-b border-[#1a1a2e]">
                          <td className="py-2 px-3 text-indigo-700">customer_id</td>
                          <td className="py-2 px-3 text-gray-600">string</td>
                          <td className="py-2 px-3"><span className="text-amber-600">recommended</span></td>
                          <td className="py-2 px-3 font-sans text-gray-600">Your unique customer/user identifier. Used to track conversations.</td>
                        </tr>
                        <tr className="border-b border-[#1a1a2e]">
                          <td className="py-2 px-3 text-indigo-700">message</td>
                          <td className="py-2 px-3 text-gray-600">string</td>
                          <td className="py-2 px-3"><span className="text-red-600">yes*</span></td>
                          <td className="py-2 px-3 font-sans text-gray-600">The message text. Required unless attachments are sent.</td>
                        </tr>
                        <tr className="border-b border-[#1a1a2e]">
                          <td className="py-2 px-3 text-indigo-700">conversation_id</td>
                          <td className="py-2 px-3 text-gray-600">string</td>
                          <td className="py-2 px-3"><span className="text-gray-400">optional</span></td>
                          <td className="py-2 px-3 font-sans text-gray-600">Pass an existing conversation_id to continue a conversation. Omit to start a new one.</td>
                        </tr>
                        <tr className="border-b border-[#1a1a2e]">
                          <td className="py-2 px-3 text-indigo-700">attachments</td>
                          <td className="py-2 px-3 text-gray-600">array</td>
                          <td className="py-2 px-3"><span className="text-gray-400">optional</span></td>
                          <td className="py-2 px-3 font-sans text-gray-600">Array of {`{ url, type, filename }`}. Types: image, file, audio, video.</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 text-indigo-700">metadata</td>
                          <td className="py-2 px-3 text-gray-600">object</td>
                          <td className="py-2 px-3"><span className="text-gray-400">optional</span></td>
                          <td className="py-2 px-3 font-sans text-gray-600">Any extra metadata to attach to the conversation.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Example */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Example (cURL)</h4>
                  <CodeBlock id="ch-curl" lang="bash" code={`curl -X POST ${API_BASE}/api/chat/public/YOUR_CHANNEL_TOKEN \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_id": "user-123",
    "message": "Hello, I need help with my order"
  }'`} />
                </div>

                {/* Sync Response */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Response (Sync Mode)</h4>
                  <CodeBlock id="ch-sync-resp" lang="json" code={`{
  "success": true,
  "data": {
    "conversation_id": "uuid-...",
    "response": "Hi! I'd be happy to help with your order. Could you share your order number?",
    "agent_id": "uuid-...",
    "agent_name": "Support Agent",
    "goal_progress": null,
    "handoff": false,
    "checkpoint": false,
    "collected_data": {},
    "status": "active"
  }
}`} />
                </div>

                {/* Async Response */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Response (Async Mode)</h4>
                  <CodeBlock id="ch-async-resp" lang="json" code={`// Immediate HTTP response:
{
  "success": true,
  "data": {
    "conversation_id": "uuid-...",
    "status": "processing"
  }
}

// Webhook POST to your URL:
{
  "event": "message.response",
  "conversation_id": "uuid-...",
  "channel_id": "uuid-...",
  "message": {
    "content": "Hi! I'd be happy to help...",
    "agent_id": "uuid-...",
    "agent_name": "Support Agent"
  },
  "metadata": {
    "goal_progress": null,
    "handoff": false,
    "checkpoint": false,
    "collected_data": {},
    "status": "active"
  },
  "timestamp": "2026-02-09T12:00:00.000Z"
}`} />
                </div>

                {/* Webhook Headers */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Webhook Delivery Headers</h4>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                    <div className="flex gap-3">
                      <code className="text-indigo-700 font-mono shrink-0">X-Webhook-Signature</code>
                      <span className="text-gray-600">HMAC-SHA256 signature of the request body, signed with the channel's webhook_secret. Base64 encoded.</span>
                    </div>
                    <div className="flex gap-3">
                      <code className="text-indigo-700 font-mono shrink-0">X-Webhook-Timestamp</code>
                      <span className="text-gray-600">ISO 8601 timestamp of when the webhook was sent.</span>
                    </div>
                    <div className="flex gap-3">
                      <code className="text-indigo-700 font-mono shrink-0">Content-Type</code>
                      <span className="text-gray-600">application/json</span>
                    </div>
                  </div>
                </div>

                {/* Verification example */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Verify Webhook Signature (Node.js)</h4>
                  <CodeBlock id="ch-verify" lang="javascript" code={`const crypto = require('crypto');

function verifyWebhook(body, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(body)
    .digest('base64');
  return signature === expected;
}

// In your webhook handler:
app.post('/webhook', (req, res) => {
  const sig = req.headers['x-webhook-signature'];
  const body = JSON.stringify(req.body);

  if (!verifyWebhook(body, sig, 'your_webhook_secret')) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const { event, conversation_id, message } = req.body;
  console.log(\`AI Response: \${message.content}\`);
  res.json({ received: true });
});`} />
                </div>

                {/* Error codes */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Error Codes</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-[#1a1a2e]"><th className="text-left py-2 px-3 text-gray-500 font-medium">HTTP</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Code</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Description</th></tr></thead>
                      <tbody className="text-xs">
                        <tr className="border-b border-[#1a1a2e]"><td className="py-2 px-3 font-mono">400</td><td className="py-2 px-3 font-mono text-red-600">VALIDATION_ERROR</td><td className="py-2 px-3 text-gray-600">Missing message or attachments</td></tr>
                        <tr className="border-b border-[#1a1a2e]"><td className="py-2 px-3 font-mono">401</td><td className="py-2 px-3 font-mono text-red-600">INVALID_TOKEN</td><td className="py-2 px-3 text-gray-600">Channel token is invalid or channel is inactive</td></tr>
                        <tr><td className="py-2 px-3 font-mono">500</td><td className="py-2 px-3 font-mono text-red-600">PROCESSING_ERROR</td><td className="py-2 px-3 text-gray-600">AI processing failed</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ─── Playground: Custom Channel Chat ──────────────────────── */}
          <div className="bg-white rounded-xl border-2 border-indigo-200 overflow-hidden">
            <div className="bg-indigo-500 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Play className="w-5 h-5" />
                Playground - Test Custom Channel
              </h3>
              <p className="text-indigo-100 text-sm mt-1">Send a message to your custom API channel and see the live response</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Channel Token <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={testToken}
                    onChange={(e) => setTestToken(e.target.value)}
                    placeholder="Paste your channel_token from Channels page"
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Customer ID</label>
                  <input
                    type="text"
                    value={testCustomerId}
                    onChange={(e) => setTestCustomerId(e.target.value)}
                    placeholder="test-customer-1"
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Message <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !testLoading && sendTestChat()}
                    placeholder="Type a message to send..."
                    className="flex-1 px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm"
                  />
                  <button
                    onClick={sendTestChat}
                    disabled={testLoading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send
                  </button>
                </div>
              </div>

              {/* Generated cURL */}
              {testToken && testMessage && (
                <div>
                  <p className="text-xs text-gray-500 mb-1.5 font-medium">Generated cURL command:</p>
                  <CodeBlock id="pg-curl" lang="bash" code={`curl -X POST ${API_BASE}/api/chat/public/${testToken.trim()} \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify({ customer_id: testCustomerId || 'test-customer', message: testMessage }, null, 2)}'`} />
                </div>
              )}

              <ResponseViewer response={testResponse} />
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          DATA INTEGRATION API SECTION
         ═══════════════════════════════════════════════════════════════ */}
      {activeSection === 'data' && (
        <div className="space-y-6">
          {/* Overview */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Data Integration API
            </h2>
            <p className="text-gray-600 mb-4">
              Push your product catalogs, FAQs, articles, or any structured data into the system so AI agents can reference it.
              Data is stored per-integration and automatically linked to a knowledge base that your agents can query.
            </p>

            {/* Flow Diagram */}
            <div className="bg-blue-50 rounded-xl p-6 mb-4">
              <h3 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide">How It Works</h3>
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                  <div>
                    <p className="font-semibold text-gray-900">Generate an API Token</p>
                    <p className="text-sm text-gray-600">Go to <strong>Settings</strong> &rarr; API Tokens &rarr; Create a new token. The token starts with <code className="bg-white px-1.5 py-0.5 rounded text-blue-700 text-xs font-mono">sk_live_</code>. Save it - it's shown only once.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                  <div>
                    <p className="font-semibold text-gray-900">Create a Custom Integration</p>
                    <p className="text-sm text-gray-600">Go to <strong>Integrations</strong> &rarr; Connect &rarr; Custom Data API. This creates an integration with a linked knowledge base. Note the <code className="bg-white px-1.5 py-0.5 rounded text-blue-700 text-xs font-mono">integration_id</code>.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
                  <div>
                    <p className="font-semibold text-gray-900">Push Data via API</p>
                    <p className="text-sm text-gray-600">POST to <code className="bg-white px-1.5 py-0.5 rounded text-blue-700 text-xs font-mono">/api/v1/data/push</code> with your API token. Push products, FAQs, articles - any structured data. Upserts by <code className="bg-white px-1.5 py-0.5 rounded text-xs font-mono">external_id</code>.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">4</div>
                  <div>
                    <p className="font-semibold text-gray-900">AI Agents Use Your Data</p>
                    <p className="text-sm text-gray-600">Once pushed, your data is available in the knowledge base. Assign the KB to an agent, and it can answer questions using your data.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Auth info */}
            <div className="bg-amber-50 border-2 border-[#1a1a2e] rounded-xl p-4">
              <div className="flex items-start gap-2">
                <Key className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">Authentication</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    All Data API endpoints require an API token in the Authorization header:<br />
                    <code className="bg-white px-2 py-0.5 rounded text-xs font-mono mt-1 inline-block">Authorization: Bearer sk_live_your_token_here</code>
                  </p>
                  <p className="text-xs text-amber-600 mt-2">
                    Rate limit: 60 requests/minute per organization on the push endpoint.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Endpoint: POST /api/v1/data/push ────────────────────── */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
            <button
              onClick={() => toggleEndpoint('data-push')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                {expandedEndpoints.has('data-push') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <MethodBadge method="POST" />
                <code className="text-sm font-mono text-gray-800">/api/v1/data/push</code>
              </div>
              <span className="text-xs text-gray-500">Push or upsert items to an integration</span>
            </button>

            {expandedEndpoints.has('data-push') && (
              <div className="border-t border-[#1a1a2e] p-5 space-y-5">
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-2 border-[#1a1a2e] rounded-lg">
                  <Key className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-700 font-medium">Requires API Token: Authorization: Bearer sk_live_...</span>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Request Body</h4>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-[#1a1a2e]"><th className="text-left py-2 px-3 text-gray-500 font-medium">Field</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Type</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Required</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Description</th></tr></thead>
                    <tbody className="font-mono text-xs">
                      <tr className="border-b border-[#1a1a2e]">
                        <td className="py-2 px-3 text-indigo-700">integration_id</td>
                        <td className="py-2 px-3 text-gray-600">string</td>
                        <td className="py-2 px-3"><span className="text-red-600">yes</span></td>
                        <td className="py-2 px-3 font-sans text-gray-600">UUID of the custom integration to push data to</td>
                      </tr>
                      <tr className="border-b border-[#1a1a2e]">
                        <td className="py-2 px-3 text-indigo-700">items</td>
                        <td className="py-2 px-3 text-gray-600">array</td>
                        <td className="py-2 px-3"><span className="text-red-600">yes</span></td>
                        <td className="py-2 px-3 font-sans text-gray-600">Array of items (max 100 per request)</td>
                      </tr>
                      <tr className="border-b border-[#1a1a2e]">
                        <td className="py-2 px-3 text-indigo-700 pl-8">items[].external_id</td>
                        <td className="py-2 px-3 text-gray-600">string</td>
                        <td className="py-2 px-3"><span className="text-red-600">yes</span></td>
                        <td className="py-2 px-3 font-sans text-gray-600">Your unique item ID. Used for upsert (same ID = update).</td>
                      </tr>
                      <tr className="border-b border-[#1a1a2e]">
                        <td className="py-2 px-3 text-indigo-700 pl-8">items[].item_type</td>
                        <td className="py-2 px-3 text-gray-600">string</td>
                        <td className="py-2 px-3"><span className="text-red-600">yes</span></td>
                        <td className="py-2 px-3 font-sans text-gray-600">Category: "product", "faq", "article", or any custom type</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 text-indigo-700 pl-8">items[].data</td>
                        <td className="py-2 px-3 text-gray-600">object</td>
                        <td className="py-2 px-3"><span className="text-red-600">yes</span></td>
                        <td className="py-2 px-3 font-sans text-gray-600">The item data (any JSON object). This is what the AI will reference.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Example (cURL)</h4>
                  <CodeBlock id="data-push-curl" lang="bash" code={`curl -X POST ${API_BASE}/api/v1/data/push \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk_live_your_token_here" \\
  -d '{
    "integration_id": "your-integration-uuid",
    "items": [
      {
        "external_id": "prod-001",
        "item_type": "product",
        "data": {
          "name": "Premium Widget",
          "price": 29.99,
          "description": "A high-quality widget",
          "category": "electronics",
          "in_stock": true
        }
      },
      {
        "external_id": "faq-001",
        "item_type": "faq",
        "data": {
          "question": "What is your return policy?",
          "answer": "30-day money-back guarantee on all items."
        }
      }
    ]
  }'`} />
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Success Response</h4>
                  <CodeBlock id="data-push-resp" lang="json" code={`{
  "success": true,
  "data": {
    "integration_id": "uuid-...",
    "created": 1,
    "updated": 1,
    "errors": []
  }
}`} />
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Error Codes</h4>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-[#1a1a2e]"><th className="text-left py-2 px-3 text-gray-500 font-medium">HTTP</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Code</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Description</th></tr></thead>
                    <tbody className="text-xs">
                      <tr className="border-b border-[#1a1a2e]"><td className="py-2 px-3 font-mono">400</td><td className="py-2 px-3 font-mono text-red-600">VALIDATION_ERROR</td><td className="py-2 px-3 text-gray-600">Missing integration_id, empty items, or items missing required fields</td></tr>
                      <tr className="border-b border-[#1a1a2e]"><td className="py-2 px-3 font-mono">401</td><td className="py-2 px-3 font-mono text-red-600">AUTHENTICATION_ERROR</td><td className="py-2 px-3 text-gray-600">Invalid or missing API token</td></tr>
                      <tr className="border-b border-[#1a1a2e]"><td className="py-2 px-3 font-mono">404</td><td className="py-2 px-3 font-mono text-red-600">NOT_FOUND</td><td className="py-2 px-3 text-gray-600">Integration not found</td></tr>
                      <tr><td className="py-2 px-3 font-mono">429</td><td className="py-2 px-3 font-mono text-red-600">RATE_LIMITED</td><td className="py-2 px-3 text-gray-600">60 requests/min exceeded</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ─── Endpoint: GET /api/v1/data/items ────────────────────── */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
            <button
              onClick={() => toggleEndpoint('data-list')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                {expandedEndpoints.has('data-list') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <MethodBadge method="GET" />
                <code className="text-sm font-mono text-gray-800">/api/v1/data/items</code>
              </div>
              <span className="text-xs text-gray-500">List items in an integration</span>
            </button>

            {expandedEndpoints.has('data-list') && (
              <div className="border-t border-[#1a1a2e] p-5 space-y-5">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Query Parameters</h4>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-[#1a1a2e]"><th className="text-left py-2 px-3 text-gray-500 font-medium">Param</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Type</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Required</th><th className="text-left py-2 px-3 text-gray-500 font-medium">Description</th></tr></thead>
                    <tbody className="font-mono text-xs">
                      <tr className="border-b border-[#1a1a2e]"><td className="py-2 px-3 text-indigo-700">integration_id</td><td className="py-2 px-3 text-gray-600">string</td><td className="py-2 px-3"><span className="text-red-600">yes</span></td><td className="py-2 px-3 font-sans text-gray-600">Integration UUID</td></tr>
                      <tr className="border-b border-[#1a1a2e]"><td className="py-2 px-3 text-indigo-700">type</td><td className="py-2 px-3 text-gray-600">string</td><td className="py-2 px-3"><span className="text-gray-400">optional</span></td><td className="py-2 px-3 font-sans text-gray-600">Filter by item_type (e.g. "product")</td></tr>
                      <tr className="border-b border-[#1a1a2e]"><td className="py-2 px-3 text-indigo-700">cursor</td><td className="py-2 px-3 text-gray-600">string</td><td className="py-2 px-3"><span className="text-gray-400">optional</span></td><td className="py-2 px-3 font-sans text-gray-600">Pagination cursor from previous response</td></tr>
                      <tr><td className="py-2 px-3 text-indigo-700">limit</td><td className="py-2 px-3 text-gray-600">number</td><td className="py-2 px-3"><span className="text-gray-400">optional</span></td><td className="py-2 px-3 font-sans text-gray-600">Items per page (default 50)</td></tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Example</h4>
                  <CodeBlock id="data-list-curl" lang="bash" code={`curl "${API_BASE}/api/v1/data/items?integration_id=YOUR_ID&type=product&limit=10" \\
  -H "Authorization: Bearer sk_live_your_token_here"`} />
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Response</h4>
                  <CodeBlock id="data-list-resp" lang="json" code={`{
  "success": true,
  "data": [
    {
      "id": "uuid-...",
      "integration_id": "uuid-...",
      "external_id": "prod-001",
      "item_type": "product",
      "data": { "name": "Premium Widget", "price": 29.99, ... },
      "last_synced_at": "2026-02-09T12:00:00.000Z",
      "created_at": "2026-02-09T12:00:00.000Z",
      "updated_at": "2026-02-09T12:00:00.000Z"
    }
  ],
  "meta": {
    "nextCursor": null,
    "total": 1,
    "limit": 50
  }
}`} />
                </div>
              </div>
            )}
          </div>

          {/* ─── Endpoint: DELETE /api/v1/data/items/:id ─────────────── */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
            <button
              onClick={() => toggleEndpoint('data-delete')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                {expandedEndpoints.has('data-delete') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <MethodBadge method="DELETE" />
                <code className="text-sm font-mono text-gray-800">/api/v1/data/items/&#123;id&#125;</code>
              </div>
              <span className="text-xs text-gray-500">Delete an item by its internal ID</span>
            </button>

            {expandedEndpoints.has('data-delete') && (
              <div className="border-t border-[#1a1a2e] p-5 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Path Parameter</h4>
                  <p className="text-sm text-gray-600"><code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs">id</code> — the internal item UUID (returned from GET /items or POST /push)</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Example</h4>
                  <CodeBlock id="data-del-curl" lang="bash" code={`curl -X DELETE ${API_BASE}/api/v1/data/items/ITEM_UUID \\
  -H "Authorization: Bearer sk_live_your_token_here"`} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Response</h4>
                  <CodeBlock id="data-del-resp" lang="json" code={`{ "success": true, "message": "Item deleted" }`} />
                </div>
              </div>
            )}
          </div>

          {/* ─── Playground: Data Push ────────────────────────────────── */}
          <div className="bg-white rounded-xl border-2 border-blue-200 overflow-hidden">
            <div className="bg-blue-500 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Play className="w-5 h-5" />
                Playground - Push Data
              </h3>
              <p className="text-blue-100 text-sm mt-1">Push items to your integration and see the result</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">API Token <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    value={dataApiKey}
                    onChange={(e) => setDataApiKey(e.target.value)}
                    placeholder="sk_live_..."
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Integration ID <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={dataIntegrationId}
                    onChange={(e) => setDataIntegrationId(e.target.value)}
                    placeholder="uuid from Integrations page"
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Items (JSON array) <span className="text-red-500">*</span></label>
                <textarea
                  value={dataItems}
                  onChange={(e) => setDataItems(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm font-mono"
                  placeholder='[{ "external_id": "...", "item_type": "...", "data": { ... } }]'
                />
              </div>

              <button
                onClick={sendDataPush}
                disabled={dataLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {dataLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Push Data
              </button>

              <ResponseViewer response={dataResponse} />
            </div>
          </div>

          {/* ─── Playground: List Items ───────────────────────────────── */}
          <div className="bg-white rounded-xl border-2 border-cyan-200 overflow-hidden">
            <div className="bg-cyan-500 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Play className="w-5 h-5" />
                Playground - List Items
              </h3>
              <p className="text-cyan-100 text-sm mt-1">Fetch items from your integration</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">API Token <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    value={listApiKey}
                    onChange={(e) => setListApiKey(e.target.value)}
                    placeholder="sk_live_..."
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Integration ID <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={listIntegrationId}
                    onChange={(e) => setListIntegrationId(e.target.value)}
                    placeholder="uuid from Integrations page"
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm font-mono"
                  />
                </div>
              </div>

              <button
                onClick={sendDataList}
                disabled={listLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-600 text-white font-semibold rounded-xl hover:bg-cyan-700 transition-colors disabled:opacity-50"
              >
                {listLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                Fetch Items
              </button>

              <ResponseViewer response={listResponse} />
            </div>
          </div>

          {/* ─── Free-Form Playground ─────────────────────────────────── */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e]/15 overflow-hidden">
            <div className="bg-gray-800 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Terminal className="w-5 h-5" />
                API Playground - Raw Request
              </h3>
              <p className="text-gray-300 text-sm mt-1">Send any custom request to any endpoint</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-3">
                <select
                  value={pgMethod}
                  onChange={(e) => setPgMethod(e.target.value as any)}
                  className="px-3 py-2.5 border-2 border-[#1a1a2e] rounded-xl text-sm font-semibold bg-white w-28"
                >
                  <option>GET</option>
                  <option>POST</option>
                  <option>DELETE</option>
                </select>
                <input
                  type="text"
                  value={pgEndpoint}
                  onChange={(e) => setPgEndpoint(e.target.value)}
                  placeholder={`${API_BASE}/api/v1/data/items?integration_id=...`}
                  className="flex-1 px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm font-mono"
                />
                <button
                  onClick={sendPlaygroundRequest}
                  disabled={pgLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800 text-white font-semibold rounded-xl hover:bg-gray-900 transition-colors disabled:opacity-50"
                >
                  {pgLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Send
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Headers (JSON)</label>
                <input
                  type="text"
                  value={pgHeaders}
                  onChange={(e) => setPgHeaders(e.target.value)}
                  placeholder='{"Authorization": "Bearer sk_live_..."}'
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm font-mono"
                />
              </div>

              {pgMethod !== 'GET' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Request Body (JSON)</label>
                  <textarea
                    value={pgBody}
                    onChange={(e) => setPgBody(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm font-mono"
                    placeholder='{ "key": "value" }'
                  />
                </div>
              )}

              <ResponseViewer response={pgResponse} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ApiDocsPage
